/** @jsxImportSource @emotion/react */

import { CellEditRequestEvent, ColDef, GetRowIdFunc } from "ag-grid-community";
import { ICellRendererParams } from "ag-grid-enterprise";
import { AgGridReact, CustomCellEditorProps } from "ag-grid-react";
import { useCallback, useMemo } from "react";
import { useToolEditorContext } from "components/ToolEditor/Provider/ToolEditorProvider";
import assert from "assert";
import { ResultControlsRenderer } from "./CellRenderers/ResultControlsRenderer";
import { FilePatternEditor } from "./FilePatterns/FilePatternsEditor";
import { ResultsEditorRowDatum } from "components/ToolEditor/ToolEditor.types";
import { MultiBadgeRenderer } from "components/ToolEditor/components/MultiBadgeRenderer";
import { isFilePatternsV3 } from "types/json-schemas/type_guards";
import { captureException } from "@sentry/react";
import { uniq } from "lodash";
import {
  CANCEL_CELL_EDIT_VALUE,
  TOOL_EDITOR_DEFAULT_COL_DEF,
} from "components/ToolEditor/constants";

export type ToolEditorResultRowCellRendererParams =
  ICellRendererParams<ResultsEditorRowDatum>;

export type ToolEditorResultRowCellEditorParams =
  CustomCellEditorProps<ResultsEditorRowDatum>;

export type ToolSpecResultsColDef = ColDef<ResultsEditorRowDatum> & {
  colId: keyof ResultsEditorRowDatum | "controls";
};

interface Props {
  rowData: ResultsEditorRowDatum[];
}

export const ResultsEditor = ({ rowData }: Props) => {
  const updateResult = useToolEditorContext((s) => s.updateResult);

  const colDefs: ToolSpecResultsColDef[] = useMemo(() => {
    return [
      {
        colId: "name" as const,
        field: "name",
        headerName: "Name",
        cellEditor: "agTextCellEditor",
        editable: true,
        width: 150,
      },
      {
        colId: "file_patterns" as const,
        field: "file_patterns",
        headerName: "File Patterns",
        cellRenderer: (params: ToolEditorResultRowCellRendererParams) => (
          <MultiBadgeRenderer items={params.data?.file_patterns ?? []} />
        ),
        cellEditor: FilePatternEditor,
        suppressKeyboardEvent: (params) => {
          if (params.event.key === "Enter" && params.editing) {
            // suppress enter to close cell editor since combo box handles enter
            return true;
          }
          return false;
        },
        editable: true,
        width: 150,
      },
      {
        colId: "description" as const,
        field: "description",
        headerName: "Description",
        cellEditor: "agTextCellEditor",
        editable: true,
        flex: 1,
      },
      {
        colId: "controls" as const,
        cellRenderer: ResultControlsRenderer,
        width: 80,
      },
    ];
  }, []);

  const handleCellEditRequest = useCallback(
    (e: CellEditRequestEvent<ResultsEditorRowDatum>) => {
      const newValue = e.newValue as unknown;
      const colId = (e.colDef as ToolSpecResultsColDef).colId;

      assert(colId !== "controls");

      if (newValue === CANCEL_CELL_EDIT_VALUE) {
        return;
      }

      switch (colId) {
        case "description":
        case "name":
          if (typeof newValue === "string") {
            return updateResult(e.data.key, colId, newValue);
          } else {
            return captureException("Unhandled Cell Edit Request", {
              extra: { key: e.data.key, colId, newValue },
            });
          }
        case "file_patterns":
          if (isFilePatternsV3(newValue)) {
            return updateResult(e.data.key, colId, uniq(newValue));
          } else {
            return captureException("Unhandled Cell Edit Request", {
              extra: { key: e.data.key, colId, newValue },
            });
          }
        case "key":
          // not editable
          return;
      }
    },
    [updateResult],
  );

  const getRowId: GetRowIdFunc<ResultsEditorRowDatum> = useCallback(
    (e) => e.data.key,
    [],
  );

  return (
    <AgGridReact<ResultsEditorRowDatum>
      className="ag-theme-balham-cell-borders"
      css={{ minWidth: 600 }}
      domLayout="autoHeight"
      columnDefs={colDefs}
      rowData={rowData}
      reactiveCustomComponents
      readOnlyEdit
      onCellEditRequest={handleCellEditRequest}
      defaultColDef={TOOL_EDITOR_DEFAULT_COL_DEF}
      getRowId={getRowId}
    />
  );
};
